import React, { useEffect, useRef } from "react";
import { useStream } from "../../../context/StreamContext";
import { useLocation } from "react-router-dom";
import View3d from "./View3d";
import { Euler, Group, MathUtils, Quaternion } from "three";
import { requestDeviceOrientation, sleep } from "./viewHelper";
import "../styling/styling.css";
import { VIEWER_LANDING_PAGE } from "../../../Constants";

export default function RenderingUnit() {
  const [stream] = useStream();
  const refContainer = useRef(null);
  const location = useLocation();
  const view3d = new View3d({
    streamThreading: location.state.streamThreading,
  });
  const screenAndBackgroundGroup = new Group();

  useEffect(() => {
    if (location.state === null) {
      window.location.href = VIEWER_LANDING_PAGE;
    }

    function loadEnvironment() {
      view3d.init();
      view3d.renderScreen({ stream: stream });
      if (location.state.background !== "/none") {
        view3d.loadTextureUsingSphere({ imagePath: location.state.background });
      }
      screenAndBackgroundGroup.add(view3d.getScreen());
      screenAndBackgroundGroup.add(view3d.getEnvironmentSphere());
      view3d.getScene().add(screenAndBackgroundGroup);
      view3d.getRenderer().setAnimationLoop(animate);
      refContainer.current.appendChild(view3d.getRenderer().domElement);

      const fps = 60;
      const interval = 1000 / fps;
      let then = performance.now();

      function animate(now) {
        const delta = now - then;

        if (delta > interval) {
          then = now - (delta % interval);
          view3d.runAnimator();
        }
      }
    }
    loadEnvironment();

    document.addEventListener("dblclick", handleViewpoinReset);
    document.addEventListener("touchstart", handlePinchStart);
    document.addEventListener("touchmove", handlePinchMove);
    window.addEventListener("resize", onWindowResize);

    return () => {
      view3d.getRenderer().dispose();
      window.removeEventListener("resize", onWindowResize);
      document.removeEventListener("touchstart", handlePinchStart);
      document.removeEventListener("touchmove", handlePinchMove);
      document.removeEventListener("dblclick", handleViewpoinReset);
    };
  }, [location.state, stream]);

  const handleViewpoinReset = (e) => {
    screenAndBackgroundGroup.rotation.setFromVector3(
      view3d.getDefaultCamera().rotation,
    );
    view3d.getScreen().position.setY(view3d.getDefaultCamera().position.y + 7);
  };

  let startY;
  const handlePinchStart = (event) => {
    event.preventDefault();
    startY = event.touches[0].pageY;
  };

  const handlePinchMove = (event) => {
    const deltaY = event.touches[0].pageY - startY;

    if (deltaY > 1 && view3d.getDefaultCamera().position.z > -10) {
      view3d.getDefaultCamera().position.z -= 0.5;
    }
    if (deltaY < -1 && view3d.getDefaultCamera().position.z < 15) {
      view3d.getDefaultCamera().position.z += 0.5;
    }
  };

  function onWindowResize(e) {
    view3d.getDefaultCamera().aspect = window.innerWidth / window.innerHeight;
    view3d.getDefaultCamera().updateProjectionMatrix();
    view3d.getRenderer().setSize(window.innerWidth, window.innerHeight);
  }

  const handleGetOrientation = async () => {
    await requestDeviceOrientation({
      handlerFunction: handleOrientationChange,
    });
    await sleep(1500);
    document.getElementById("overlay").style.display = "none";
    handleViewpoinReset();
  };

  let gammaNormalser = undefined;
  let quaternion = new Quaternion();
  let eulerRotationSpec = new Euler();
  const handleOrientationChange = (orientationEvent) => {
    const gamma = -MathUtils.degToRad(orientationEvent.gamma);

    if (gammaNormalser === undefined) {
      gammaNormalser = gamma > 0 ? 1 : -1;
    }

    const alpha = MathUtils.degToRad(orientationEvent.alpha);

    if (gammaNormalser === 1 && gamma > 0) {
      eulerRotationSpec.x = gammaNormalser * gamma;
      eulerRotationSpec.y = alpha;
      quaternion.setFromEuler(eulerRotationSpec, false);
      view3d.getDefaultCamera().setRotationFromQuaternion(quaternion);
    }

    if (gammaNormalser === -1 && gamma < 0) {
      eulerRotationSpec.x = gammaNormalser * gamma;
      eulerRotationSpec.y = alpha;
      quaternion.setFromEuler(eulerRotationSpec, false);
      view3d.getDefaultCamera().setRotationFromQuaternion(quaternion);
    }
  };

  return (
    <div>
      <div id="overlay">
        <h2>Please use in landscape mode</h2>
        <h3 className="paddingtop20" style={{ color: "red" }}>
          <u>
            Warning: Contains bright flashing lights that may trigger
            photosensitive epileptic seizures. Viewer discretion is adviced.
          </u>
        </h3>
        <h2 className="paddingtop20">
          Application would like to track device orientation
        </h2>
        <br />
        <button onClick={handleGetOrientation}>Accept</button>
      </div>

      <div id="container" ref={refContainer}></div>
    </div>
  );
}
